import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from '../../../utils/theme-context'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { tryToFetchLeoCostsForClient, tryToFetchLeoStats } from '../../../store/costs/costsSlice'
import { useGetAllUserConfigsQuery, useGetUserAIModelsQuery } from '../../../store/chatbox/chatboxApi'
import type { ClientDetailsFilters, PlatformOption } from './components/ClientCostsDetailsFiltersBar'
import ClientCostsDetailsFiltersBar from './components/ClientCostsDetailsFiltersBar'
import ClientCostsDashboard from './components/ClientCostsDashboard'


const ClientCostsDetailsPage = () => {
    const navigate = useNavigate()
    const { clientId } = useParams()
    const dispatch = useAppDispatch()
    const { theme } = useTheme()
    const mode = theme === 'dark' ? 'dark' : 'light'
    const loading = useAppSelector((s) => s.clientCosts?.detailsLoading)
    const data = useAppSelector((s) => s.clientCosts.clientDetails)
    const dataStats = useAppSelector((s) => s.clientCosts.stats)
    const { data: models } = useGetUserAIModelsQuery({ userId: clientId })
    const { data: dataConfig } = useGetAllUserConfigsQuery(clientId)
    const [selection, setSelection] = useState<any>()
    const [properties, setProperties] = useState<any>()
    const [filters, setFilters] = useState<ClientDetailsFilters>({
        startDate: undefined,
        endDate: undefined,
        provider: undefined,
    })

    useEffect(() => {
        if (dataConfig?.data) {
            setProperties(
                dataConfig?.data?.map((item: any) => ({
                    name: item?.propertyName || item?.companyName,
                    id: item?._id,
                    scenario: item?.scenario || null,
                })) || [],
            )
        }
    }, [dataConfig?.data])

    const platforms: PlatformOption[] = useMemo(() => {
        return (models?.data || [])
            .map((m: any) => {
                const id = m?.id || m?._id
                if (!id) return null
                return {
                    label: (m?.name || '').toLowerCase() === 'chatgpt' ? 'ChatGPT' : m?.name,
                    name: m?.name,
                    value: String(id),
                    icon: m?.logoUrl,
                }
            })
            .filter(Boolean)
    }, [models])

    const payload = useMemo(() => {
        return {
            clientId,
            data: {
                startDate: filters.startDate,
                endDate: filters.endDate,
                provider: filters.provider,
            },
        }
    }, [clientId, filters.endDate, filters.provider, filters.startDate])

    useEffect(() => {
        if (!clientId) return
        let req: any = { clientId, ...filters || {} }
        if (selection && selection?.type !== 'all') {
            req = { ...req || {}, property: selection?.id }
        }
        dispatch(tryToFetchLeoStats(req)).unwrap()
        dispatch(tryToFetchLeoCostsForClient(req))
    }, [clientId, dispatch, payload, selection, filters])

    const onApply = async () => {
        if (!clientId) return
        await dispatch(tryToFetchLeoCostsForClient(payload)).unwrap()
    }

    const totals = data?.totals

    const StatPill = ({ label, value }: { label: string; value: string }) => (
        <div className='inline-flex items-center gap-2 rounded-full border border-gray-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-3 py-1 text-xs text-gray-700 dark:text-gray-200 backdrop-blur'>
            <span className='text-gray-500 dark:text-gray-400'>{label}</span>
            <span className='tabular-nums font-semibold'>{value}</span>
        </div>
    )

    return (
        <div className='w-full'>
            <div className='flex flex-col gap-4'>

                <div className='flex flex-row justify-between items-start'>
                    <div className='flex items-start gap-3'>
                        <button
                            type='button'
                            onClick={() => navigate('/costs')}
                            className='mt-1 cursor-pointer rounded-md p-1 hover:bg-gray-100 dark:hover:bg-white/10'
                            aria-label='Back to costs'
                        >
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='22'
                                height='22'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='text-black dark:text-white'
                            >
                                <path d='M15 18l-6-6 6-6' />
                            </svg>
                        </button>

                        <div className='flex flex-col gap-1'>
                            <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                                Client Leo LLM Costs Details
                            </h1>
                            <p className='text-sm text-gray-600 dark:text-gray-300'>
                                Costs shown in GBP (£) for selected date range and platform.
                            </p>
                        </div>
                    </div>
                    <div>
                        <StatPill label='Start Date:' value={moment(data?.stats?.filtersApplied?.dbStartDate).format('DD-MM-YYYY')} />

                    </div>
                </div>
                <ClientCostsDetailsFiltersBar
                    properties={properties}
                    selection={selection}
                    setSelection={setSelection}
                    value={filters}
                    platforms={platforms}
                    onChange={setFilters}
                    onApply={onApply}
                    loading={loading}
                />

                <ClientCostsDashboard
                    mode={mode}
                    loading={loading}
                    clientDetails={data}
                    stats={dataStats?.data || dataStats}
                    totals={totals}
                    platforms={platforms}
                />
            </div>
        </div>
    )
}

export default ClientCostsDetailsPage